/**
 * Compatibility wrapper for next-routes Link
 * Converts old next-routes API to React Router Link
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrgSlug } from '../lib/utils.js';
import * as ROUTES from '../constants/routes.js';

// Complete route name to path template mapping (from old old-routes.js)
const routeMap = {
  // Auth routes
  'login/callback': ROUTES.LOGIN_CALLBACK,
  'login/sso-redirect': ROUTES.LOGIN_SSO_REDIRECT,
  'forgot-password': ROUTES.FORGOT_PASSWORD,
  'reset-password': ROUTES.RESET_PASSWORD,
  index: ROUTES.INDEX,

  // Project routes
  'projects/index': ROUTES.PROJECTS_INDEX,
  'projects/archived': ROUTES.PROJECTS_ARCHIVED,
  'projects/new': ROUTES.PROJECTS_NEW,
  'projects/show': ROUTES.PROJECTS_SHOW,
  'projects/edit': ROUTES.PROJECTS_EDIT,
  'projects/files': ROUTES.PROJECTS_FILES,
  'projects/initiatives/show': ROUTES.PROJECTS_INITIATIVES,

  // Change Impact routes
  'projects/change-impacts': ROUTES.CHANGE_IMPACTS_INDEX,
  'projects/change-impacts/data-import': ROUTES.CHANGE_IMPACTS_DATA_IMPORT,
  'projects/change-impacts/new': ROUTES.CHANGE_IMPACTS_NEW,
  'projects/change-impacts/edit': ROUTES.CHANGE_IMPACTS_EDIT,
  'projects/change-impacts/show': ROUTES.CHANGE_IMPACTS_SHOW,
  'projects/change-impacts/tasks/new': ROUTES.TASKS_NEW,
  'projects/change-impacts/tasks/edit': ROUTES.TASKS_EDIT,
  'projects/change-impacts/tasks/show': ROUTES.TASKS_SHOW,

  // Stakeholder routes
  'projects/stakeholders': ROUTES.STAKEHOLDERS_INDEX,
  'projects/stakeholders/settings': ROUTES.STAKEHOLDERS_SETTINGS,
  'projects/stakeholders/show': ROUTES.STAKEHOLDERS_SHOW,
  'projects/stakeholders/new/step-stakeholder':
    ROUTES.STAKEHOLDERS_NEW_STEP_STAKEHOLDER,
  'projects/stakeholders/new/step-information':
    ROUTES.STAKEHOLDERS_NEW_STEP_INFORMATION,
  'projects/stakeholders/new/step-group-participants':
    ROUTES.STAKEHOLDERS_NEW_STEP_GROUP_PARTICIPANTS,
  'projects/stakeholders/new/step-communicators':
    ROUTES.STAKEHOLDERS_NEW_STEP_COMMUNICATORS,
  'projects/stakeholders/edit/step-stakeholder':
    ROUTES.STAKEHOLDERS_EDIT_STEP_STAKEHOLDER,
  'projects/stakeholders/edit/step-information':
    ROUTES.STAKEHOLDERS_EDIT_STEP_INFORMATION,
  'projects/stakeholders/edit/step-group-participants':
    ROUTES.STAKEHOLDERS_EDIT_STEP_GROUP_PARTICIPANTS,
  'projects/stakeholders/edit/step-communicators':
    ROUTES.STAKEHOLDERS_EDIT_STEP_COMMUNICATORS,

  // Change Strategy routes
  'projects/change-strategy': ROUTES.CHANGE_STRATEGY_INDEX,
  'projects/change-strategy/new': ROUTES.CHANGE_STRATEGY_INDEX,
  'projects/change-strategy/edit': ROUTES.CHANGE_STRATEGY_INDEX,
  'projects/change-strategy/show': ROUTES.CHANGE_STRATEGY_INDEX,
  'projects/change-strategy/settings': ROUTES.CHANGE_STRATEGY_SETTINGS,
  'projects/change-strategy/new/step-general':
    ROUTES.CHANGE_STRATEGY_NEW_STEP_GENERAL,
  'projects/change-strategy/new/step-details':
    ROUTES.CHANGE_STRATEGY_NEW_STEP_DETAILS,
  'projects/change-strategy/new/step-governance':
    ROUTES.CHANGE_STRATEGY_NEW_STEP_GOVERNANCE,
  'projects/change-strategy/new/step-milestones':
    ROUTES.CHANGE_STRATEGY_NEW_STEP_MILESTONES,
  'projects/change-strategy/new/step-linked-files':
    ROUTES.CHANGE_STRATEGY_NEW_STEP_LINKED_FILES,
  'projects/change-strategy/edit/step-general':
    ROUTES.CHANGE_STRATEGY_EDIT_STEP_GENERAL,
  'projects/change-strategy/edit/step-details':
    ROUTES.CHANGE_STRATEGY_EDIT_STEP_DETAILS,
  'projects/change-strategy/edit/step-governance':
    ROUTES.CHANGE_STRATEGY_EDIT_STEP_GOVERNANCE,
  'projects/change-strategy/edit/step-milestones':
    ROUTES.CHANGE_STRATEGY_EDIT_STEP_MILESTONES,
  'projects/change-strategy/edit/step-linked-files':
    ROUTES.CHANGE_STRATEGY_EDIT_STEP_LINKED_FILES,

  // Portfolio routes
  'parent-portfolio/new': ROUTES.PARENT_PORTFOLIO_NEW,
  'parent-portfolio/edit': ROUTES.PARENT_PORTFOLIO_EDIT,
  'portfolio/new/step-general': ROUTES.PORTFOLIO_NEW_STEP_GENERAL,
  'portfolio/new/step-details': ROUTES.PORTFOLIO_NEW_STEP_DETAILS,
  'portfolio/new/step-milestones': ROUTES.PORTFOLIO_NEW_STEP_MILESTONES,
  'portfolio/new/step-linked-files': ROUTES.PORTFOLIO_NEW_STEP_LINKED_FILES,
  'portfolio/edit/step-general': ROUTES.PORTFOLIO_EDIT_STEP_GENERAL,
  'portfolio/edit/step-details': ROUTES.PORTFOLIO_EDIT_STEP_DETAILS,
  'portfolio/edit/step-milestones': ROUTES.PORTFOLIO_EDIT_STEP_MILESTONES,
  'portfolio/edit/step-linked-files': ROUTES.PORTFOLIO_EDIT_STEP_LINKED_FILES,
  'portfolio/settings': ROUTES.PORTFOLIO_SETTINGS,
  'portfolio/show/info': ROUTES.PORTFOLIO_INFO,
  'portfolio/show/projects': ROUTES.PORTFOLIO_PROJECTS,

  // Initiative routes (with type patterns)
  'initiatives/new': ROUTES.INITIATIVES_NEW,
  'initiatives/show': ROUTES.INITIATIVES_SHOW,
  'initiatives/preview': ROUTES.INITIATIVES_PREVIEW,
  'initiatives/edit': ROUTES.INITIATIVES_EDIT,
  'initiatives/files': ROUTES.INITIATIVES_FILES,

  // Files
  'files/new': ROUTES.FILES_NEW,
  'files/edit': ROUTES.FILES_EDIT,

  // Employees
  'employees/show': ROUTES.EMPLOYEES_SHOW,
  'employees/change-impacts': ROUTES.EMPLOYEES_CHANGE_IMPACTS,
  'employees/tasks': ROUTES.EMPLOYEES_TASKS,
  'employees/stakeholder-profiles': ROUTES.EMPLOYEES_STAKEHOLDER_PROFILES,

  // Reports
  'project-reports': ROUTES.PROJECT_REPORTS,
  'change-impact-reports': ROUTES.CHANGE_IMPACT_REPORTS,

  // Organization routes
  'organization/show': ROUTES.ORGANIZATION_SHOW,
  'organization/edit': ROUTES.ORGANIZATION_EDIT,
  'organization/custom-settings': ROUTES.ORGANIZATION_CUSTOM_SETTINGS,
  'organization/stakeholders': ROUTES.ORGANIZATION_STAKEHOLDERS,
  'organization/stakeholders/new': ROUTES.ORGANIZATION_STAKEHOLDERS_NEW,
  'organization/users': ROUTES.ORGANIZATION_USERS,
  'organization/users/new': ROUTES.ORGANIZATION_USERS_NEW,
  'organization/users/show': ROUTES.ORGANIZATION_USERS_SHOW,
  'organization/users/edit': ROUTES.ORGANIZATION_USERS_EDIT,
  'organization/users/permissions/show':
    ROUTES.ORGANIZATION_USERS_PERMISSIONS_SHOW,
  'organization/users/permissions/new':
    ROUTES.ORGANIZATION_USERS_PERMISSIONS_NEW,
  'organization/users/permissions/edit':
    ROUTES.ORGANIZATION_USERS_PERMISSIONS_EDIT,
  'organization/employees': ROUTES.ORGANIZATION_EMPLOYEES,
  'organization/employees/edit': ROUTES.ORGANIZATION_EMPLOYEES_EDIT,
  'organization/custom-settings/statuses/edit':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_STATUSES_EDIT,
  'organization/custom-settings/categories/edit':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CATEGORIES_EDIT,
  'organization/custom-settings/change-impact-types/edit':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_IMPACT_TYPES_EDIT,
  'organization/custom-settings/organization-settings/edit':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_ORGANIZATION_SETTINGS_EDIT,

  // Change Insights routes
  'change-insights/studio/show': ROUTES.CHANGE_INSIGHTS_STUDIO_SHOW,
  'change-insights/studio/respondent-answers':
    ROUTES.CHANGE_INSIGHTS_STUDIO_RESPONDENT_ANSWERS,
  'change-insights/studio/settings': ROUTES.CHANGE_INSIGHTS_STUDIO_SETTINGS,
  'change-insights/studio/preview': ROUTES.CHANGE_INSIGHTS_STUDIO_PREVIEW,
  'change-insights/edit': ROUTES.CHANGE_INSIGHTS_EDIT,
  'change-insights/new': ROUTES.CHANGE_INSIGHTS_NEW,
  'change-insights/analysis/show': ROUTES.CHANGE_INSIGHTS_ANALYSIS_SHOW,
  'organization/custom-settings/change-insights/new':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_NEW,
  'organization/custom-settings/change-insights/edit':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_EDIT,
  'organization/custom-settings/change-insights/preview-new':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_PREVIEW_NEW,
  'organization/custom-settings/change-insights/preview-edit':
    ROUTES.ORGANIZATION_CUSTOM_SETTINGS_CHANGE_INSIGHTS_PREVIEW_EDIT,

  // Survey routes
  survey: ROUTES.SURVEY,
  'survey-response': ROUTES.SURVEY_RESPONSE,
  'survey-submit-email': ROUTES.SURVEY_SUBMIT_EMAIL,

  // Other
  'online-events': ROUTES.ONLINE_EVENTS,
};

export function buildPath(route, params = {}) {
  // If route is already a path pattern (starts with /), use it directly
  // Otherwise look it up in the routeMap
  let path = route.startsWith('/') ? route : (routeMap[route] || route);

  // Auto-inject slug if path needs it but it's not provided
  if (path.includes(':slug') && !params.slug) {
    const currentSlug = getOrgSlug();
    // Only inject if we actually have a valid slug
    if (currentSlug) {
      params = { ...params, slug: currentSlug };
    }
  }

  // Separate path params from query params
  const queryParams = {};
  const pathParams = { ...params };

  // Replace path parameters (like :slug, :projectId)
  Object.keys(pathParams).forEach((key) => {
    const paramPlaceholder = `:${key}`;

    if (path.includes(paramPlaceholder)) {
      // This is a path parameter
      if (pathParams[key] != null) {
        path = path.replace(paramPlaceholder, pathParams[key]);
      }
    } else {
      // This is a query parameter
      // Keep false, 0, but skip null, undefined, empty string
      if (
        pathParams[key] !== null &&
        pathParams[key] !== undefined &&
        pathParams[key] !== ''
      ) {
        queryParams[key] = pathParams[key];
      }
    }
  });

  // Build query string from remaining params
  if (Object.keys(queryParams).length > 0) {
    const searchParams = new URLSearchParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // Convert arrays to slash-delimited strings (next-routes compatibility)
        // [82, 620, 621] → "82/620/621"
        if (value.length > 0) {
          searchParams.append(key, value.join('/'));
        }
      } else {
        searchParams.append(key, value);
      }
    });

    const queryString = searchParams.toString();
    if (queryString) {
      path = `${path}?${queryString}`;
    }
  }

  return path;
}

/**
 * Link component that mimics next-routes API
 *
 * Usage:
 *   <Link route="projects/index" params={{ slug: 'org' }}>
 *     <a>Projects</a>
 *   </Link>
 *
 * Or direct path:
 *   <Link to="/projects">Projects</Link>
 */
const Link = ({
  route,
  params,
  to,
  children,
  prefetch,
  shallow,
  scroll,
  replace,
  locale,
  ...props
}) => {
  const navigate = useNavigate();

  // Filter out Next.js specific props that don't apply to React Router
  // prefetch, shallow are Next.js Link props

  let path;

  if (to) {
    // Direct path provided (React Router style)
    path = to;
  } else if (route) {
    // Check if route is actually a full path (starts with /)
    // This handles legacy usage where route={`/${slug}/...`} was used
    if (route.startsWith('/')) {
      // It's a full path, use it directly
      path = route;
    } else {
      // It's a route name, build path from route map
      path = buildPath(route, params);
    }
  } else {
    path = '/';
  }

  // If children is a function, call it with href
  if (typeof children === 'function') {
    return children({ href: path });
  }

  const handleClick = (e) => {
    // Check if user passed onClick prop, merge with navigation
    if (props.onClick) {
      props.onClick(e);
    }

    // Only navigate if not prevented
    if (!e.defaultPrevented) {
      e.preventDefault();
      navigate(path);
    }
  };

  return (
    <a
      href={path}
      onClick={handleClick}
      // style={{ color: 'inherit' }}
      {...props}
    >
      {children}
    </a>
  );
};

export default Link;
export { Link };
